import React, { useCallback, useState } from "react";
import ArrowDown from "@bloomberg/media-ui-iconography/ArrowDown";
import ArrowUp from "@bloomberg/media-ui-iconography/ArrowUp";
import ChevronRight from "@bloomberg/media-ui-iconography/ChevronRight";

import { Tracker } from "@util/tracker";

import * as List from "../list/index";

import { DropdownProps, FilterableListProps } from "./types";
import { useDropdown } from "./useDropdown";

import styles from "./styles.module.css";

export const Dropdown = (props: DropdownProps) => {
  const { options, selectedFilter, handleDropdownSelect } = props;

  const [isOpen, setIsOpen] = useState(false);

  const handleClick = useCallback(
    (option: string) => {
      handleDropdownSelect(option);
      setIsOpen(false);
    },
    [handleDropdownSelect]
  );

  const tracker = Tracker.getInstance();

  return (
    <div className={styles.dropdown}>
      <button
        className={styles.dropdownButton}
        onClick={() => setIsOpen(!isOpen)}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="listbox"
        ref={tracker.registerTracking}
      >
        <div className={styles.text}>{selectedFilter}</div>
        {isOpen ? <ArrowUp /> : <ArrowDown />}
      </button>
      {isOpen && (
        <ul className={styles.dropdownMenu} id="listbox" role="listbox">
          {options.map((option) => (
            <li
              key={option}
              value={option}
              className={styles.dropdownOption}
              onClick={() => handleClick(option)}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

Dropdown.displayName = "FilterableList.Dropdown";

export const StyledTitle = ({
  title,
}: {
  title: { text: string; url?: string };
}) => {
  const { text, url } = title;

  const tracker = Tracker.getInstance();

  if (url) {
    return (
      <div className={styles.titleLink}>
        <a href={url} ref={tracker.registerTracking}>
          {text}
        </a>
      </div>
    );
  }

  return <h3 className={styles.title}>{text}</h3>;
};

export const StyledBottomLink = ({
  link,
}: {
  link: { text: string; url: string };
}) => {
  const tracker = Tracker.getInstance();

  return (
    <div className={styles.bottomLink}>
      <a href={link.url} ref={tracker.registerTracking}>
        {link.text}
      </a>
      <ChevronRight className={styles.bottomIcon} />
    </div>
  );
};

export function validateProps<IterableComponentProps>(
  props: FilterableListProps<IterableComponentProps>
) {
  if (!Object.keys(props.filterFunctions).length) {
    console.error(`No filtering options passed in.`);

    return false;
  }

  return true;
}

export function Component<IterableComponentProps>(
  props: FilterableListProps<IterableComponentProps>
) {
  const { filterFunctions, link, title } = props;

  if (!validateProps(props)) return null;

  const { componentPropsList, selectedFilter, error, handleDropdownSelect } =
    useDropdown({ filterFunctions });

  if (error) console.error(error);

  return (
    <div className={styles.filterableListContainer}>
      <div className={styles.header}>
        {title && <StyledTitle title={title} />}
        <Dropdown
          options={Object.keys(props.filterFunctions)}
          selectedFilter={selectedFilter}
          handleDropdownSelect={handleDropdownSelect}
        />
      </div>
      <List.Component<IterableComponentProps>
        {...props}
        componentPropsList={componentPropsList}
      />
      {link && <StyledBottomLink link={link} />}
    </div>
  );
}
Component.displayName = "FilterableList.Component";
